import React, { useMemo } from 'react';
import classNames from 'classnames';
import { ListingPackage } from 'models/Listing';
import { LISTING_PACKAGE_DATA } from './data';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import {
  checkIsNoPackage,
  checkIsTierInitiallyPlatinum,
  checkIsTierInitiallyPremium,
} from 'utils';
import { BrokerLogo } from './components/BrokerLogo';
import { BrokerAvatar } from './components/BrokerAvatar';
import { SIZE } from 'models/Size';
import useMatchMedia from 'hooks/useMatchMedia';

import './ListingOptionCard.styles.scss';
import './ListingOptionCard.styles.responsive.scss';

export type ListingOptionCardProps = {
  className?: string;
  option: ListingPackage;
  isSelected: boolean;
  brokerLogo?: string;
  brokerAvatar?: string;
  onClick: () => void;
};

const ListingOptionCard: React.FC<ListingOptionCardProps> = (props) => {
  const { className, option, isSelected, brokerAvatar, brokerLogo, onClick } =
    props;

  const { color, title, time, description, price, listOfPerks } =
    LISTING_PACKAGE_DATA[option];

  const { t } = useTranslation();

  const classes = classNames(
    'white-panel',
    'listing-option-card',
    color,
    { selected: isSelected },
    className,
  );

  const isPhablet = useMatchMedia('isPhablet');

  const isListingNoPackage = checkIsNoPackage(option);

  const isListingPlatinum = checkIsTierInitiallyPlatinum(option);
  const isListingPremium = checkIsTierInitiallyPremium(option);
  const isListingPlatinumOrPremium = isListingPlatinum || isListingPremium;

  const EnchancedBranding = useMemo(
    () =>
      isListingPlatinum && (
        <span className="enhanced-branding">{t('premiumBranding')}</span>
      ),
    [isListingPlatinum, t],
  );

  const BrokerInfo = useMemo(
    () =>
      isListingPlatinumOrPremium && (
        <div className="broker-info">
          {isListingPlatinum && <BrokerAvatar brokerAvatarUrl={brokerAvatar} />}
          <BrokerLogo
            brokerLogoUrl={brokerLogo}
            size={isListingPlatinum ? SIZE.BIG : SIZE.SMALL}
          />
        </div>
      ),
    [brokerAvatar, brokerLogo, isListingPlatinum, isListingPlatinumOrPremium],
  );

  return (
    <div className={classes}>
      {isListingNoPackage ? (
        <span>{t(title)}</span>
      ) : (
        <div>
          <p className="title">{t(title)}</p>
          <p className="description">{t(description)}</p>
          <p className="price">
            {price} {isPhablet && EnchancedBranding}
          </p>
          {isPhablet && BrokerInfo}
        </div>
      )}

      {listOfPerks?.length && (
        <ul>
          {time && (
            <li className="time">
              <CheckcircleIcon />
              {t(time)}
            </li>
          )}
          {listOfPerks.map((perk: string) => (
            <li key={perk}>
              <CheckcircleIcon />
              {t(perk)}
            </li>
          ))}
        </ul>
      )}

      <div>
        {!isPhablet && (
          <>
            {BrokerInfo} {EnchancedBranding}
          </>
        )}

        <Button
          onClick={onClick}
          iconPosition="right"
          icon={isSelected ? CheckcircleIcon : null}
        >
          {t(isSelected ? 'selected' : 'select')}
        </Button>
      </div>
    </div>
  );
};

export default ListingOptionCard;
